import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { http } from '../../lib/http';
import { lookupPlanPrice, sumAddOnsPrice, fetchLivePricing } from '../../lib/pricing';
import { Button } from '../../components/ui/button';
import * as XLSX from 'xlsx';
import CustomersTab from '../../components/admin/CustomersTab';
import NewSignupsTab from '../../components/admin/NewSignupsTab';
import RenewalsTab from '../../components/admin/RenewalsTab';
import ExClientsTab from '../../components/admin/ExClientsTab';
import UpdateRequestsTab from '../../components/admin/UpdateRequestsTab';
import DispatchingTab from '../../components/admin/DispatchingTab';
import NotificationsTab from '../../components/admin/NotificationsTab';
import ActivityLogTab from '../../components/admin/ActivityLogTab';
import MessagesTab from '../../components/admin/MessagesTab';
import WeeklyMenuTab from '../../components/admin/WeeklyMenuTab';
import IncomeExpensesTab from '../../components/admin/IncomeExpensesTab';
import PreferencesTab from '../../components/admin/PreferencesTab';
import DriverManagementTab from '../../components/admin/DriverManagementTab';
import TrainingGuideTab from '../../components/admin/TrainingGuideTab';
import PowerQuoteTab from '../../components/admin/PowerQuoteTab';
import CancellationRequestsTab from '../../components/shared/CancellationRequestsTab';
import CustomerPasswordsTab from '../../components/admin/CustomerPasswordsTab';


const AdminDashboard = ({ onSwitchDashboard }) => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('customers');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotificationsDropdown, setShowNotificationsDropdown] = useState(false);

  // Admin Chat state
  const [showChatBox, setShowChatBox] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [newChatMessage, setNewChatMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [firstUnreadIndex, setFirstUnreadIndex] = useState(null);

  // New Signups count state
  const [signupsCount, setSignupsCount] = useState(0);
  const [updateRequestsCount, setUpdateRequestsCount] = useState(0);
  const [notificationsCount, setNotificationsCount] = useState(0);
  const [cancellationCount, setCancellationCount] = useState(0);

  // Admin Notes state
  const [showNotesPanel, setShowNotesPanel] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [notesLastUpdated, setNotesLastUpdated] = useState(null);
  const [notesUpdatedBy, setNotesUpdatedBy] = useState(null);
  const [savingNotes, setSavingNotes] = useState(false);

  // Load notifications
  const loadNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await http.get('/admin/notifications', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setNotifications(response.data.notifications || []);
        setUnreadCount(response.data.unread_count || 0);
        // Count unread notifications
        const unread = (response.data.notifications || []).filter(n => !n.read).length;
        setNotificationsCount(unread);
      }
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  };

  // Load signups count
  const loadSignupsCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await http.get('/admin/signups', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.todayCount !== undefined) {
        setSignupsCount(response.data.todayCount);
      } else if (response.data.data && response.data.data.signups) {
        setSignupsCount(response.data.data.signups.filter(s => s.is_today).length || 0);
      } else if (response.data.signups) {
        // Fallback just in case todayCount isn't there
        setSignupsCount(response.data.signups.filter(s => s.is_today).length || 0);
      }
    } catch (error) {
      console.error('Failed to load signups count:', error);
    }
  };

  // Load update requests count
  const loadUpdateRequestsCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await http.get('/admin/update-requests', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.requests) {
        // Count all requests that need admin attention or review
        const pending = response.data.requests.filter(r =>
          r.status === 'pending_attention' || r.status === 'applied_automatically' || r.status === 'pending'
        ).length;
        setUpdateRequestsCount(pending);
      }
    } catch (error) {
      console.error('Failed to load update requests count:', error);
    }
  };

  // Load cancellation requests count
  const loadCancellationCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await http.get('/admin/cancellation-requests', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.data?.requests) {
        const pending = response.data.data.requests.filter(r => r.status === 'pending_attention').length;
        setCancellationCount(pending);
      }
    } catch (error) {
      console.error('Failed to load cancellation count:', error);
    }
  };

  // Load admin notes
  const loadAdminNotes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await http.get('/admin/notes', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setAdminNotes(response.data.notes || '');
        setNotesLastUpdated(response.data.last_updated);
        setNotesUpdatedBy(response.data.updated_by);
      }
    } catch (error) {
      console.error('Failed to load admin notes:', error);
    }
  };

  // Save admin notes
  const saveAdminNotes = async () => {
    try {
      setSavingNotes(true);
      const token = localStorage.getItem('token');
      const response = await http.put('/admin/notes',
        { notes: adminNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setNotesLastUpdated(response.data.last_updated);
        setNotesUpdatedBy(response.data.updated_by);
        setMessage('Notes saved successfully! ✓');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      console.error('Failed to save admin notes:', error);
      setError('Failed to save notes');
      setTimeout(() => setError(''), 3000);
    } finally {
      setSavingNotes(false);
    }
  };

  // Load notifications on mount and periodically
  useEffect(() => {
    // Load live pricing on mount
    fetchLivePricing().then(() => {
      console.log('✅ Live pricing loaded for Admin Dashboard');
    });

    loadNotifications();
    loadSignupsCount();
    loadUpdateRequestsCount();
    loadCancellationCount();
    loadAdminNotes();
    const notifInterval = setInterval(loadNotifications, 30000); // Refresh every 30 seconds
    const signupsInterval = setInterval(loadSignupsCount, 60000); // Refresh every 60 seconds
    const updatesInterval = setInterval(loadUpdateRequestsCount, 60000); // Refresh every 60 seconds
    const cancellationInterval = setInterval(loadCancellationCount, 60000);
    const notesInterval = setInterval(loadAdminNotes, 60000); // Refresh notes every 60 seconds
    return () => {
      clearInterval(notifInterval);
      clearInterval(signupsInterval);
      clearInterval(updatesInterval);
      clearInterval(cancellationInterval);
      clearInterval(notesInterval);
    };
  }, []);

  // Clear individual notification
  const clearNotification = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await http.post(`/admin/notifications/${notificationId}/clear`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      loadNotifications();
    } catch (error) {
      console.error('Failed to clear notification:', error);
    }
  };

  // Clear all notifications
  const clearAllNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      await http.post('/admin/notifications/clear-all', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      loadNotifications();
    } catch (error) {
      console.error('Failed to clear all notifications:', error);
    }
  };

  // Admin Chat functions
  const loadChatMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await http.get('/admin/chat/messages?limit=50', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setChatMessages(response.data.messages || []);
        setFirstUnreadIndex(response.data.first_unread_index);
      }
    } catch (error) {
      console.error('Failed to load chat messages:', error);
    }
  };

  const loadChatUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await http.get('/admin/chat/unread-count', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setChatUnreadCount(response.data.unread_count || 0);
      }
    } catch (error) {
      console.error('Failed to load chat unread count:', error);
    }
  };

  const markMessagesAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await http.post('/admin/chat/mark-read', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setChatUnreadCount(0);
      setFirstUnreadIndex(null);
    } catch (error) {
      console.error('Failed to mark messages as read:', error);
    }
  };

  const sendChatMessage = async () => {
    if (!newChatMessage.trim()) return;

    try {
      setSendingMessage(true);
      const token = localStorage.getItem('token');

      const messageData = {
        message: newChatMessage
      };

      // Add reply information if replying to a message
      if (replyingTo) {
        messageData.reply_to_id = replyingTo.id;
      }

      await http.post('/admin/chat/messages',
        messageData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setNewChatMessage('');
      setReplyingTo(null); // Clear reply state
      await loadChatMessages();

      // Scroll to bottom of chat
      setTimeout(() => {
        const chatContainer = document.getElementById('chat-messages-container');
        if (chatContainer) {
          chatContainer.scrollTop = chatContainer.scrollHeight;
        }
      }, 100);
    } catch (error) {
      console.error('Failed to send message:', error);
      setError('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const deleteChatMessage = async (messageId) => {
    try {
      const token = localStorage.getItem('token');
      await http.delete(`/admin/chat/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      await loadChatMessages();
      await loadChatUnreadCount();
    } catch (error) {
      console.error('Failed to delete message:', error);
    }
  };

  const clearAllChats = async () => {
    if (!window.confirm('Are you sure you want to clear all chat messages? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await http.delete('/admin/chat/clear-all', {
        headers: { Authorization: `Bearer ${token}` }
      });

      setChatMessages([]);
      setChatUnreadCount(0);
      setFirstUnreadIndex(null);
      setMessage('All chat messages cleared');
    } catch (error) {
      console.error('Failed to clear all chats:', error);
      setError('Failed to clear all chats');
    }
  };

  // Load chat messages and mark as read when chat is opened
  useEffect(() => {
    if (showChatBox) {
      loadChatMessages();
      markMessagesAsRead(); // Mark messages as read when opening chat

      // Refresh messages every 10 seconds while chat is open
      const interval = setInterval(() => {
        loadChatMessages();
        markMessagesAsRead();
      }, 10000);

      return () => clearInterval(interval);
    }
  }, [showChatBox]);

  // Load chat unread count on component mount and periodically
  useEffect(() => {
    loadChatUnreadCount();

    // Refresh chat unread count every 15 seconds
    const interval = setInterval(loadChatUnreadCount, 15000);
    return () => clearInterval(interval);
  }, []);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (message || error) {
      const timer = setTimeout(() => {
        setMessage('');
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message, error]);

  if (!user || (user.role !== 'admin' && user.role !== 'owner')) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-orange-500">Access Denied</h2>
          <p className="text-gray-700">Admin access required</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-black border-b-2 border-orange-500 shadow-lg shadow-orange-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <img
                src="/images/just-protein-logo.png"
                alt="Just Protein"
                className="h-10 w-10 sm:h-12 sm:w-12 rounded-full border-2 border-orange-500 shadow-lg shadow-orange-500/50"
              />
              <div>
                <h1 className="text-xl sm:text-3xl font-bold text-white">Just Protein</h1>
                <p className="text-xs sm:text-sm text-gray-300">Admin Dashboard</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
              <div className="flex items-center gap-2">
                <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full font-semibold">{user?.role?.toUpperCase()}</span>
                <span className="text-white text-xs sm:text-sm truncate max-w-[150px]">{user?.email}</span>
              </div>

              {/* Navigation Buttons */}
              <div className="flex flex-wrap gap-2">
                {user?.role === 'owner' && (
                  <button
                    onClick={() => onSwitchDashboard && onSwitchDashboard('owner')}
                    className="px-2 sm:px-4 py-1 sm:py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-md border-2 border-orange-600 transition-colors text-xs sm:text-sm"
                  >
                    Owner
                  </button>
                )}

                {onSwitchDashboard && (
                  <button
                    onClick={() => onSwitchDashboard('selector')}
                    className="px-2 sm:px-4 py-1 sm:py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md border-2 border-purple-700 transition-colors text-xs sm:text-sm font-bold"
                  >
                    Switch View
                  </button>
                )}

                <button
                  onClick={logout}
                  className="px-2 sm:px-4 py-1 sm:py-2 bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center space-x-1 text-xs sm:text-sm"
                >
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      {(message || error) && (
        <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 max-w-sm w-full px-4 sm:px-0">
          {message && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative shadow-lg animate-fade-in-down">
              <span className="block sm:inline">{message}</span>
              <button onClick={() => setMessage('')} className="absolute top-0 right-0 px-4 py-3">
                <span className="text-xl leading-none">&times;</span>
              </button>
            </div>
          )}

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative shadow-lg animate-fade-in-down">
              <span className="block sm:inline">{error}</span>
              <button onClick={() => setError('')} className="absolute top-0 right-0 px-4 py-3">
                <span className="text-xl leading-none">&times;</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="bg-white rounded-xl shadow-lg border-2 border-gray-100 p-2 mb-6">
          <nav className="flex space-x-1 sm:space-x-2 overflow-x-auto pb-2 scrollbar-hide">
            {[
              { id: 'new-signups', label: 'New Signups', badge: signupsCount > 0 ? signupsCount : null },
              { id: 'customers', label: 'Customers', badge: null },
              { id: 'ex-clients', label: 'Ex-Clients', badge: null },
              { id: 'updates', label: 'Update Requests', badge: updateRequestsCount > 0 ? updateRequestsCount : null },
              { id: 'cancellations', label: 'Cancellation Requests', badge: cancellationCount > 0 ? cancellationCount : null },
              { id: 'renewals', label: 'Renewals', badge: null },
              { id: 'dispatching', label: 'Dispatching', badge: null },
              { id: 'preferences', label: 'Preferences', badge: null },
              { id: 'notifications', label: 'Notifications', badge: notificationsCount > 0 ? notificationsCount : null },
              { id: 'income-expenses', label: 'Income & Expenses', badge: null },
              { id: 'messages', label: 'Messages', badge: null },
              { id: 'driver-management', label: 'Driver Management', badge: null },
              { id: 'weekly-menu', label: 'Weekly Menu', badge: null },
              { id: 'training-guide', label: 'Training Guide', badge: null },
              { id: 'customer-passwords', label: 'Customer Passwords', badge: null }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 sm:px-6 py-2 sm:py-3 font-semibold transition-all whitespace-nowrap text-xs sm:text-base rounded-lg ${activeTab === tab.id
                  ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg'
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-black'
                  }`}
              >
                {tab.label}
                {tab.badge && (
                  <span className="ml-2 inline-block px-2 py-1 text-xs font-semibold text-white bg-orange-600 rounded-full">
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content with Sidebar Support */}
        <div className="mt-6">
          {/* Main Content Area */}
          <div className="w-full">
            {activeTab === 'customers' && <CustomersTab setMessage={setMessage} setError={setError} />}
            {activeTab === 'new-signups' && <NewSignupsTab setMessage={setMessage} setError={setError} onSignupsChange={loadSignupsCount} />}
            {activeTab === 'renewals' && <RenewalsTab setMessage={setMessage} setError={setError} />}
            {activeTab === 'ex-clients' && <ExClientsTab setMessage={setMessage} setError={setError} />}
            {activeTab === 'updates' && <UpdateRequestsTab setMessage={setMessage} setError={setError} onRequestsChange={loadUpdateRequestsCount} />}
            {activeTab === 'cancellations' && <CancellationRequestsTab apiBase="/admin" setMessage={setMessage} setError={setError} onCountChange={loadCancellationCount} />}
            {activeTab === 'dispatching' && <DispatchingTab setMessage={setMessage} setError={setError} />}
            {activeTab === 'income-expenses' && <IncomeExpensesTab setMessage={setMessage} setError={setError} />}
            {activeTab === 'notifications' && <ActivityLogTab setMessage={setMessage} setError={setError} endpoint="/admin/activity-logs" />}
            {activeTab === 'messages' && <MessagesTab setMessage={setMessage} setError={setError} />}
            {activeTab === 'weekly-menu' && <WeeklyMenuTab setMessage={setMessage} setError={setError} />}
            {activeTab === 'preferences' && <PreferencesTab setMessage={setMessage} setError={setError} />}
            {activeTab === 'driver-management' && <DriverManagementTab setMessage={setMessage} setError={setError} />}
            {activeTab === 'training-guide' && <TrainingGuideTab />}
            {activeTab === 'customer-passwords' && <CustomerPasswordsTab setMessage={setMessage} setError={setError} />}
          </div>
        </div>
      </div>

      {/* Floating Action Buttons */}
      <div className="fixed bottom-6 right-6 flex flex-col gap-3 z-50">
        {/* Admin Notes Button */}
        <button
          onClick={() => setShowNotesPanel(!showNotesPanel)}
          className="bg-orange-500 hover:bg-orange-600 text-white rounded-full p-4 shadow-lg transition-transform hover:scale-110"
          title="Admin Notes"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>

        {/* Admin Chat Button */}
        <button
          onClick={() => setShowChatBox(!showChatBox)}
          className="bg-orange-500 hover:bg-orange-600 text-white rounded-full p-4 shadow-lg transition-transform hover:scale-110"
          title="Admin Chat"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          {chatUnreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
              {chatUnreadCount > 9 ? '9+' : chatUnreadCount}
            </span>
          )}
        </button>
      </div>

      {/* Chat Box */}
      {showChatBox && (
        <div className="fixed bottom-24 right-6 w-96 h-[500px] bg-white rounded-lg shadow-2xl z-50 flex flex-col border-2 border-orange-500">
          {/* Chat Header */}
          <div className="bg-orange-500 text-white p-4 rounded-t-lg flex justify-between items-center">
            <h3 className="font-bold text-lg">Admin Chat</h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={clearAllChats}
                className="text-white hover:text-gray-200 text-sm"
                title="Clear all chats"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
              <button
                onClick={() => setShowChatBox(false)}
                className="text-white hover:text-gray-200"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Chat Messages */}
          <div
            id="chat-messages-container"
            className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50"
          >
            {chatMessages.length === 0 ? (
              <div className="text-center text-gray-500 mt-8">
                <p>No messages yet</p>
                <p className="text-sm">Start a conversation!</p>
              </div>
            ) : (
              chatMessages.map((msg, index) => {
                const isOwnMessage = msg.user_id === user?.id;
                const showUnreadDivider = firstUnreadIndex !== null && index === firstUnreadIndex;

                return (
                  <React.Fragment key={msg.id}>
                    {/* Unread Messages Divider */}
                    {showUnreadDivider && (
                      <div className="flex items-center my-4">
                        <div className="flex-1 border-t-2 border-red-500"></div>
                        <span className="px-3 text-xs font-semibold text-red-500 bg-red-50 rounded-full py-1">
                          Unread messages start here
                        </span>
                        <div className="flex-1 border-t-2 border-red-500"></div>
                      </div>
                    )}

                    <div
                      className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg p-3 ${isOwnMessage
                          ? 'bg-orange-500 text-white'
                          : 'bg-white border border-gray-200'
                          }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-xs font-semibold ${isOwnMessage ? 'text-orange-100' : 'text-gray-600'}`}>
                            {isOwnMessage ? 'You' : msg.user_name || msg.user_email}
                          </span>
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => setReplyingTo(msg)}
                              className={`${isOwnMessage ? 'text-orange-100 hover:text-white' : 'text-gray-400 hover:text-blue-500'}`}
                              title="Reply to this message"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                              </svg>
                            </button>
                            {(isOwnMessage || user?.role === 'owner') && (
                              <button
                                onClick={() => deleteChatMessage(msg.id)}
                                className={`${isOwnMessage ? 'text-orange-100 hover:text-white' : 'text-gray-400 hover:text-red-500'}`}
                                title="Delete message"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Show replied-to message if exists */}
                        {msg.reply_to && (
                          <div className={`text-xs mb-2 p-2 rounded ${isOwnMessage ? 'bg-orange-600' : 'bg-gray-100'} border-l-2 ${isOwnMessage ? 'border-orange-300' : 'border-gray-400'}`}>
                            <div className={`font-semibold ${isOwnMessage ? 'text-orange-100' : 'text-gray-600'}`}>
                              ↩ Replying to {msg.reply_to.user_name}
                            </div>
                            <div className={`${isOwnMessage ? 'text-orange-100' : 'text-gray-600'} truncate`}>
                              {msg.reply_to.message}
                            </div>
                          </div>
                        )}

                        <p className={`text-sm ${isOwnMessage ? 'text-white' : 'text-gray-800'}`}>
                          {msg.message}
                        </p>
                        <span className={`text-xs ${isOwnMessage ? 'text-orange-100' : 'text-gray-400'} mt-1 block`}>
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })
            )}
          </div>

          {/* Chat Input */}
          <div className="p-4 border-t border-gray-200 bg-white rounded-b-lg">
            {/* Replying To Indicator */}
            {replyingTo && (
              <div className="mb-2 p-2 bg-orange-50 border-l-4 border-orange-500 rounded flex items-start justify-between">
                <div className="flex-1">
                  <div className="text-xs font-semibold text-orange-700">
                    ↩ Replying to {replyingTo.user_name || replyingTo.user_email}
                  </div>
                  <div className="text-xs text-gray-600 truncate">
                    {replyingTo.message}
                  </div>
                </div>
                <button
                  onClick={() => setReplyingTo(null)}
                  className="text-gray-400 hover:text-gray-600 ml-2"
                  title="Cancel reply"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

            <div className="flex space-x-2">
              <input
                type="text"
                value={newChatMessage}
                onChange={(e) => setNewChatMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !sendingMessage) {
                    sendChatMessage();
                  }
                }}
                placeholder="Type a message..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                disabled={sendingMessage}
              />
              <button
                onClick={sendChatMessage}
                disabled={sendingMessage || !newChatMessage.trim()}
                className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sendingMessage ? (
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Notes Slide-in Panel */}
      {showNotesPanel && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-30 z-40 transition-opacity"
            onClick={() => setShowNotesPanel(false)}
          ></div>

          {/* Slide-in Panel */}
          <div
            className="fixed top-0 right-0 h-full w-96 bg-gradient-to-br from-orange-50 to-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out"
            style={{ animation: 'slideIn 0.3s ease-out' }}
          >
            {/* Panel Header */}
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4 flex justify-between items-center">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Admin Notes
              </h3>
              <button
                onClick={() => setShowNotesPanel(false)}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Panel Content */}
            <div className="p-6 flex flex-col h-full">
              <div className="text-sm text-gray-600 mb-3 bg-orange-100 p-3 rounded-lg border border-orange-200">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline mr-2 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Shared notepad for all admins. Changes are visible to everyone.
              </div>

              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                onBlur={saveAdminNotes}
                placeholder="Type your notes here...&#10;&#10;• Important reminders&#10;• Tasks to complete&#10;• Information to share with team"
                className="flex-1 w-full p-4 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:ring-2 focus:ring-orange-200 resize-none text-sm mb-4"
                style={{ minHeight: '400px' }}
              />

              {/* Footer */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <button
                    onClick={saveAdminNotes}
                    disabled={savingNotes}
                    className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md"
                  >
                    {savingNotes ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </span>
                    ) : (
                      'Save Notes'
                    )}
                  </button>

                  <button
                    onClick={() => setShowNotesPanel(false)}
                    className="text-gray-600 hover:text-gray-800 px-4 py-2 rounded-lg transition-colors"
                  >
                    Close
                  </button>
                </div>

                {notesLastUpdated && (
                  <div className="text-xs text-gray-600 bg-gray-100 p-2 rounded">
                    <div className="flex items-center gap-1">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Last updated by: <span className="font-semibold">{notesUpdatedBy}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
